import { useMemo } from "react";
import { Icon } from "@salestrak/ui";
import type { EntityItemConfig, EntitySelectorConfig } from "@salestrak/ui";
import {
  FIELD_TYPE_ORDER,
  getFieldTypeMeta,
} from "#app/lib/constants/field-type-icons";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";

/** Pool id shared by every custom-field-type EntitySelector. */
export const FIELD_TYPE_POOL_ID = "field-types";

/**
 * Types the engine registers whose editor is not built yet, with the reason a
 * reader sees. Every create surface lists these DISABLED — never hidden — so
 * a reader looking for the type learns it exists and is on its way, and the
 * rows a picker offers do not change shape the day the type turns on. This
 * is the one place a type's availability is decided; a picker that hides or
 * greys a type on its own drifts from the others.
 */
export const UNAVAILABLE_FIELD_TYPES: Readonly<Record<string, string>> = {
  formula: "Coming soon",
};

/** The reason a type cannot be created yet, or null when it can. */
export function fieldTypeUnavailableReason(type: string): string | null {
  return UNAVAILABLE_FIELD_TYPES[type] ?? null;
}

/**
 * The custom-field types offered in create/type-picker UIs, with `photos`
 * dropped when the Photos feature flag is off for the workspace. Single source
 * for every type selector and the type-grouped fields table so the flag can't
 * be honored in one place and missed in another. Unavailable types STAY in
 * the list — the pickers render them disabled through
 * {@link fieldTypeUnavailableReason}.
 */
export function useFieldTypeOrder(): string[] {
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);
  return useMemo(
    () =>
      photosEnabled
        ? FIELD_TYPE_ORDER
        : FIELD_TYPE_ORDER.filter((type) => type !== "photos"),
    [photosEnabled],
  );
}

/**
 * EntitySelector config for the "create custom field" type picker, built from
 * {@link useFieldTypeOrder} so `photos` disappears with the feature flag. Used
 * by every create flow (settings page, manage dialog, form dialog). An
 * unavailable type is a disabled row whose tooltip carries the reason.
 */
export function useFieldTypeSelectorConfig(): EntitySelectorConfig {
  const fieldTypeOrder = useFieldTypeOrder();
  return useMemo(() => {
    const items: EntityItemConfig[] = fieldTypeOrder.map((type) => {
      const meta = getFieldTypeMeta(type);
      const unavailable = fieldTypeUnavailableReason(type);
      return {
        id: type,
        title: meta.label,
        ...(unavailable ? { disabled: true, tooltip: unavailable } : {}),
        avatar: {
          icon: meta.icon,
          color: meta.color,
          // Bare glyph in the type's key color (pv2) — no tile behind it;
          // icon/color stay alongside as data for selection round-trips.
          render: () => <Icon name={meta.icon} size="md" color={meta.color} />,
        },
      };
    });
    return {
      pools: [{ id: FIELD_TYPE_POOL_ID, label: "All", items }],
      maxSelections: 1,
      searchPlaceholder: "Search field types...",
      emptyMessage: "No matching field types",
      // Vocabulary list, not entity rows — titles read at normal weight (pv2).
      itemTitleWeight: "normal",
    };
  }, [fieldTypeOrder]);
}
